<template>
  <div
    ref="root"
    class="accessible-tooltip"
    :class="[
      `accessible-tooltip--${position}`,
      { 'accessible-tooltip--block': block },
    ]"
    @mouseenter="handlePointerEnter"
    @mouseleave="handlePointerLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <slot
      name="trigger"
      :tooltip-id="tooltipId"
      :visible="visible"
    />

    <div
      v-show="visible"
      :id="tooltipId"
      class="accessible-tooltip__content"
      role="tooltip"
      @mouseenter="handleTooltipEnter"
      @mouseleave="handleTooltipLeave"
    >
      <span class="accessible-tooltip__bubble">
        {{ text }}
      </span>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance, onBeforeUnmount, ref, watch } from "vue";

export default {
  name: "AccessibleTooltip",

  props: {
    text: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: "bottom",
      validator: (value) => ["top", "right", "bottom", "left"].includes(value),
    },
    block: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const root = ref(null);
    const visible = ref(false);
    const pointerInside = ref(false);
    const focusInside = ref(false);
    const dismissed = ref(false);

    const instance = getCurrentInstance();
    const tooltipId = `accessible-tooltip-${instance.uid}`;

    let hideTimer = null;

    const cancelScheduledHide = () => {
      if (hideTimer !== null) {
        window.clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const show = () => {
      cancelScheduledHide();

      if (!dismissed.value) {
        visible.value = true;
      }
    };

    const hideWhenInactive = () => {
      cancelScheduledHide();

      hideTimer = window.setTimeout(() => {
        if (!pointerInside.value && !focusInside.value) {
          visible.value = false;
          dismissed.value = false;
        }

        hideTimer = null;
      }, 200);
    };

    const handlePointerEnter = () => {
      pointerInside.value = true;
      show();
    };

    const handlePointerLeave = () => {
      pointerInside.value = false;
      hideWhenInactive();
    };

    const handleTooltipEnter = () => {
      pointerInside.value = true;
      show();
    };

    const handleTooltipLeave = () => {
      pointerInside.value = false;
      hideWhenInactive();
    };

    const handleFocusIn = () => {
      focusInside.value = true;
      show();
    };

    const handleFocusOut = (event) => {
      if (root.value?.contains(event.relatedTarget)) {
        return;
      }

      focusInside.value = false;
      hideWhenInactive();
    };

    const dismiss = () => {
      cancelScheduledHide();
      dismissed.value = true;
      visible.value = false;
    };

    const handleDocumentKeydown = (event) => {
      if (event.key !== "Escape" || !visible.value) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      dismiss();
    };

    watch(visible, (isVisible) => {
      if (isVisible) {
        window.addEventListener("keydown", handleDocumentKeydown, true);
      } else {
        window.removeEventListener("keydown", handleDocumentKeydown, true);
      }
    });

    onBeforeUnmount(() => {
      cancelScheduledHide();
      window.removeEventListener("keydown", handleDocumentKeydown, true);
    });

    return {
      root,
      visible,
      tooltipId,
      handlePointerEnter,
      handlePointerLeave,
      handleTooltipEnter,
      handleTooltipLeave,
      handleFocusIn,
      handleFocusOut,
      dismiss,
    };
  },
};
</script>